import { useState } from "react";
import { SecurityClassType1, SecurityDetailsPosition, SecurityTag, SecurityTypeCode } from "api/holdings/types";
import { Option } from "components/Select/Select";
import { useDetectActiveUserType } from "hooks/useDetectActiveUserType";
import i18n from "i18next";
import { isProfessionalClassification } from "utils/userClassification";

export enum EOrderType {
    MARKET_ORDER = "Order type-Market",
    LIMIT_ORDER = "Order type-Limit",
}

export enum EOrderClassType {
    COMPLEX = "Complex",
    NON_COMPLEX = "Non-complex",
}

export enum EOrderExpiration {
    DAY = "Order expiration-Day",
    GOOD_TILL_CANCELLED = "Order expiration-Good til cancelled",
}

const getOrderTypeOptions = (): Option[] => {
    return [
        { id: EOrderType.MARKET_ORDER, label: i18n.t("tradingModal.marketOrder") },
        { id: EOrderType.LIMIT_ORDER, label: i18n.t("tradingModal.limitOrder") },
    ];
};

const getOrderExpirationOptions = (): Option[] => {
    return [
        { id: EOrderExpiration.DAY, label: i18n.t("tradingModal.day") },
        { id: EOrderExpiration.GOOD_TILL_CANCELLED, label: i18n.t("tradingModal.goodTillCancelled") },
    ];
};

export const useOrderTypeFields = (
    selectedOrderTypeId?: string | undefined,
    selectedOrderExpirationId?: string | undefined,
    initialUnitPrice?: number | undefined,
) => {
    const orderTypeOptions = getOrderTypeOptions();
    const orderExpirationOptions = getOrderExpirationOptions();

    const [orderType, setOrderType] = useState<Option | undefined>(selectedOrderTypeId ?
        orderTypeOptions.find((option: Option) => option.id === selectedOrderTypeId)
        : orderTypeOptions[0]);

    const [orderExpiration, setOrderExpiration] = useState<Option | undefined>(selectedOrderExpirationId ?
        orderExpirationOptions.find((option: Option) => option.id === selectedOrderExpirationId)
        : orderExpirationOptions[0]);

    const [unitPrice, setUnitPrice] = useState<number | undefined>(initialUnitPrice ?? 0);

    const [isConfirmationChecked, setConfirmationIsChecked] = useState(false);

    const { isAnotherLegalUser, contactData } = useDetectActiveUserType();

    const handleSetOrderType = (option?: Option) => {
        setOrderType(option);
        if (option?.id === EOrderType.MARKET_ORDER) {
            setOrderExpiration(orderExpirationOptions.find((o) => o.id === EOrderExpiration.DAY));
        }
    };

    const handleSetOrderExpiration = (option?: Option) => {
        setOrderExpiration(option);
    };

    const handleSetUnitPrice = (value?: number) => {
        setUnitPrice(value);
    };

    const isOrderTypeFieldsVisible = (securityType: SecurityTypeCode | undefined): boolean => {
        return securityType ? [
            SecurityTypeCode.EQUITY,
            SecurityTypeCode.ETF,
            SecurityTypeCode.DEBT_INSTRUMENT,
        ].includes(securityType) : false;
    }

    const handleCheckboxChange = (checked: boolean) => {
        setConfirmationIsChecked(checked);
    };

    const getDisclaimerKeys = (
        classType: string, 
        securityType: SecurityTypeCode | undefined, 
        isCBL: boolean | undefined,
        classificationCode: string | undefined
    ) => {
        const isComplex = classType === SecurityClassType1.COMPLEX;
        let keyPrefix = '';

        if (isCBL) {
            keyPrefix = 'tradingModal.disclaimerC_CBL';
        } else {
            switch (securityType) {
                case SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE:
                case SecurityTypeCode.ETF:
                    keyPrefix = isComplex ? 'tradingModal.disclaimerComplexTypeC_CE' : 'tradingModal.disclaimerNonComplexTypeC_CE';
                    break;
                case SecurityTypeCode.DEBT_INSTRUMENT:
                case SecurityTypeCode.EQUITY:
                    keyPrefix = isComplex ? 'tradingModal.disclaimerComplexD_E' : 'tradingModal.disclaimerNonComplexD_E';
                    break;
            }
        }

        if (isProfessionalClassification(classificationCode)) 
        {
            keyPrefix = 'tradingModal.disclaimerPROF';
        }

        const disclaimerKeysMap: { [key: string]: string[] } = {
            'tradingModal.disclaimerC_CBL': ['first_row', 'second_row', 'third_row', 'fourth_row', 'fifth_row'],
            'tradingModal.disclaimerNonComplexTypeC_CE': ['first_row', 'second_row', 'third_row', 'fourth_row', 'fifth_row'],
            'tradingModal.disclaimerComplexD_E': ['first_row', 'second_row', 'third_row'],
            'tradingModal.disclaimerComplexTypeC_CE': ['first_row', 'second_row', 'third_row', 'fourth_row'],
            'tradingModal.disclaimerNonComplexD_E': ['first_row', 'second_row', 'third_row', 'fourth_row'],
            'tradingModal.disclaimerPROF': ['first_row', 'second_row']
        };

        const translationDisclaimerKeys = disclaimerKeysMap[keyPrefix] || [];

        return {
            keyPrefix,
            translationDisclaimerKeys
        };
    };

    const getLocaleTag = (): string => {
        const locale = i18n.language;

        const localeMapping: { [key: string]: string } = {
            "lv-LV": "lv_LV",
            "en-US": "en_US",
            "lt-LT": "lt_LT"
        };

        const mappedLocale = localeMapping[locale] || "en_US";

        return `,apitradeorder.OrderLanguage-${mappedLocale}`;
    }


    const getFormattedOrderTypeFields = (security: SecurityDetailsPosition | null, isConfirmationChecked = false): { tags: string | undefined; } => {
        const securityType = security?.type.code;
        let extraTag = isConfirmationChecked ? getLocaleTag() + ",apitradeorder.PortalApprTestWarningAccepted" : getLocaleTag();
        
        if (isAnotherLegalUser && contactData?.name) {
            extraTag += `,apitradeorder.OrderEnteredById-${contactData.contactId},apitradeorder.OrderEnteredBy-${contactData.name}`;
        }

        if ([
            SecurityTypeCode.DEBT_INSTRUMENT,
            SecurityTypeCode.EQUITY
        ].includes(securityType as SecurityTypeCode) && security?.tagsAsSet.includes(SecurityTag.SUBSCR_PORTAL_GROUP)) {
            return {
                tags: `apitradeorder.Order type-Limit,apitradeorder.Order expiration-Good til cancelled,apitradeorder.Order source-Client Portal${extraTag}`,
            }
        }

        if (isOrderTypeFieldsVisible(securityType)) {
            let tags: string | undefined = orderType?.id ? `apitradeorder.${orderType.id.toString()}` : undefined;

            if (orderExpiration) {
                tags += `,apitradeorder.${orderExpiration?.id},apitradeorder.Order source-Client Portal`;
            }

            const result = {
                tags: tags + extraTag,
            }

            return result;
        }

        return {
            tags: `apitradeorder.Order source-Client Portal${extraTag}`,
        };
    }

    return {
        orderType: orderType,
        orderTypeOptions: orderTypeOptions,
        setOrderType: handleSetOrderType,

        orderExpiration: orderExpiration,
        orderExpirationOptions: orderExpirationOptions,
        setOrderExpiration: handleSetOrderExpiration,

        unitPrice: unitPrice,
        setUnitPrice: handleSetUnitPrice,

        isConfirmationChecked: isConfirmationChecked,
        setConfirmationIsChecked: handleCheckboxChange,

        getFormattedOrderTypeFields: getFormattedOrderTypeFields,
        isOrderTypeFieldsVisible: isOrderTypeFieldsVisible,

        getDisclaimerKeys: getDisclaimerKeys,
    };
};
